import customCheckboxRadioSwitchStyles from 'assets/jss/material-dashboard-pro-react/customCheckboxRadioSwitch';
import styles from 'assets/jss/material-dashboard-pro-react/modalStyle';
import { useCampaignDetail } from 'data/campaign';
import { Field, Form, Formik } from 'formik';
import _ from 'lodash';
import PropTypes from 'prop-types';
import React from 'react';
import { toast } from 'sonner';
import { ECampaignStatus } from 'types/campaign';
import * as Yup from 'yup';
import 'utils/yupExtensions';

import { ButtonBase } from '@material-ui/core';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Radio from '@material-ui/core/Radio';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import Tooltip from '@material-ui/core/Tooltip';
import FiberManualRecordIcon from '@material-ui/icons/FiberManualRecord';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import PersonIcon from '@material-ui/icons/Person';

import CustomInputDashboard from 'creative-components/CustomInput/CustomInputDashboard';

import ActionModal from 'components/ActionModal/ActionModal';
import { addressYupValidationWithoutLocation, initialAddress } from 'components/AddressAutocomplete/AddressForm';
import ContactFields from 'components/ContactFields/ContactFields';
import LoadingIndicator from 'components/LoadingIndicator/LoadingIndicator';
import { useLoadingIndicatorContext } from 'components/LoadingIndicator/LoadingIndicatorProvider';

import { updateCampaignEventOverride } from 'utils/api';
import { nameRegExpGeneral, numberWithCommas, phoneRegExp } from 'utils/lib';

const useStyles = makeStyles((theme) => ({
  ...styles(theme),
  ...customCheckboxRadioSwitchStyles(theme),
  warningText: {
    fontSize: '14px',
    color: theme.palette.warning.main,
    fontWeight: 'bold',
    textAlign: 'center',
  },
  contactFieldsContainer: {
    margin: '20px -16px 0',
  },
  subtitle: {
    display: 'flex',
    alignItems: 'center',
    color: theme.palette.darkGray.main,
    fontWeight: 600,
    fontSize: '16px',
    lineHeight: '20px',

    '& > svg': {
      marginLeft: '10px',
    },
  },
  cancelButton: {
    backgroundColor: `${theme.palette.grayScale6.main} !important`,
  },
}));

const EditCampaignEventOverrideModal = ({
  campaignId, monthlyRatePerApn = null, eventId, defaultOwnerContactInfo, existingEventOverride, onCancel,
}) => {
  const theme = useTheme();
  const classes = useStyles();
  const { showLoadingIndicatorModal, hideLoadingIndicatorModal } = useLoadingIndicatorContext();

  const { campaign, mutateCampaign } = useCampaignDetail(campaignId);

  const [initialFarmingStoppedVal] = React.useState(existingEventOverride ? existingEventOverride.farmingStopped : false);

  return (
    <Formik
      validateOnChange={false}
      initialValues={{
        newOwnerContact: existingEventOverride && existingEventOverride.newOwnerContact
          ? ({
            ..._.pick(existingEventOverride.newOwnerContact, ['firstName', 'lastName', 'email', 'phoneNumber']),
            address: {
              ..._.pick(existingEventOverride.newOwnerContact.address, ['address1', 'address2', 'city', 'state', 'zipcode']),
              location: {}, // Include this so AddressForm doesn't complain
            },
          })
          : ({
            ..._.pick(defaultOwnerContactInfo, ['firstName', 'lastName', 'email', 'phoneNumber']),
            address: {
              ..._.pick(defaultOwnerContactInfo.address, ['address1', 'address2', 'city', 'state', 'zipcode']),
              location: {}, // Include this so AddressForm doesn't complain
            },
          }),
        farmingStopped: initialFarmingStoppedVal,
        notes: existingEventOverride ? existingEventOverride.notes : '',
      }}
      onSubmit={async ({ newOwnerContact, farmingStopped, notes }) => {
        try {
          showLoadingIndicatorModal();

          const { eventOverride: updatedEventOverride } = await updateCampaignEventOverride(campaignId, eventId, {
            ...newOwnerContact,
            // Don't send address object if address is not filled out
            // We don't store location with the address for owner contact infos
            address: newOwnerContact.address.address1 ? _.omit(newOwnerContact.address, 'location') : undefined,
          }, farmingStopped, notes);

          // Refetch campaign to update monthly cost and override data
          await mutateCampaign();

          toast.success('Your changes have been saved');

          onCancel();
        } catch (error) {
          if (error.response && error.response.data.error) {
            if (error.response.data.error.includes('must be a valid email')) {
              toast.error('Please enter a valid email address');
            } else {
              toast.error(error.response.data.error);
            }
          } else {
            toast.error('Something went wrong, please try again.');
          }

          console.error('error', error);
        } finally {
          hideLoadingIndicatorModal();
        }
      }}
      validationSchema={Yup.object().shape({
        newOwnerContact: Yup.object().shape({
          firstName: Yup.string().required().matches(nameRegExpGeneral),
          lastName: Yup.string().matches(nameRegExpGeneral), // Not required for owner contact info
          email: Yup.string().email(), // Not required for owner contact info
          phoneNumber: Yup.string().matches(phoneRegExp, 'Phone number is not valid'), // Not required for owner contact info
          address: Yup.lazy((value) => {
            // Address is optional here. It can be cleared to stop sending letters/postcards to a recipient.
            // Either the address is all null or all its fields are required.
            const addressValue = value || {};
            const requiredFields = ['address1', 'city', 'state', 'zipcode'];
            const hasAnyRequiredField = requiredFields.some((field) => {
              const fieldValue = _.get(addressValue, field);
              return typeof fieldValue === 'string' ? fieldValue.trim() !== '' : Boolean(fieldValue);
            });

            if (!hasAnyRequiredField) {
              return Yup.object().shape({
                address1: Yup.string().nullable(),
                address2: Yup.string().nullable(),
                city: Yup.string().nullable(),
                state: Yup.string().nullable(),
                zipcode: Yup.string().nullable(),
                location: Yup.mixed().nullable(),
              });
            }

            return addressYupValidationWithoutLocation;
          }),
        }).required(),
        farmingStopped: Yup.boolean().required(),
        notes: Yup.string().optional(),
      })}
    >
      {(props) => {
        const {
          touched, errors, dirty, isValid, handleSubmit, isSubmitting, setFieldValue, values,
        } = props;

        return (
          <Form>
            <ActionModal
              maxWidth="md"
              backgroundColor={theme.palette.white.main}
              textColor={theme.palette.darkGray.main}
              icon={<PersonIcon />}
              onClose={onCancel}
              title="Edit Prospect"
              message={(
                <>
                  {isSubmitting ? <LoadingIndicator modal /> : null}

                  {/* Only allow farming status changes for active/paused campaigns */}
                  {[ECampaignStatus.Active, ECampaignStatus.Paused].includes(campaign.status) && (
                    <>
                      <div className={classes.subtitle}>
                        Farming Status
                        <Tooltip
                          placement="top"
                          disableFocusListener
                          disableTouchListener
                          title={'If farming is disabled, campaign marketing touches won\'t be sent to this lead'}
                        >
                          <InfoOutlinedIcon />
                        </Tooltip>
                      </div>
                      <Field
                        name="farmingStopped"
                      >
                        {({ field }) => (
                          <>
                            <FormControlLabel
                              control={(
                                <Radio
                                  {...field}
                                  checked={!field.value}
                                  onChange={() => setFieldValue(field.name, false, true)}
                                  icon={(
                                    <FiberManualRecordIcon
                                      className={classes.radioUnchecked}
                                    />
                                  )}
                                  checkedIcon={(
                                    <FiberManualRecordIcon
                                      className={classes.radioChecked}
                                    />
                                  )}
                                  classes={{
                                    checked: classes.radio,
                                    root: classes.radioRoot,
                                  }}
                                />
                              )}
                              classes={{
                                label: classes.label,
                                root: classes.labelRoot,
                              }}
                              label="Enabled"
                            />
                            <FormControlLabel
                              control={(
                                <Radio
                                  {...field}
                                  checked={field.value}
                                  onChange={() => setFieldValue(field.name, true, true)}
                                  icon={(
                                    <FiberManualRecordIcon
                                      className={classes.radioUnchecked}
                                    />
                                  )}
                                  checkedIcon={(
                                    <FiberManualRecordIcon
                                      className={classes.radioChecked}
                                    />
                                  )}
                                  classes={{
                                    checked: classes.radio,
                                    root: classes.radioRoot,
                                  }}
                                  style={{ marginLeft: '20px' }}
                                />
                              )}
                              classes={{
                                label: classes.label,
                                root: classes.labelRoot,
                              }}
                              label="Disabled"
                            />
                          </>
                        )}
                      </Field>
                      {monthlyRatePerApn && initialFarmingStoppedVal !== values.farmingStopped && (
                        <div
                          className={classes.subtitle}
                          style={{
                            fontWeight: 500,
                            fontSize: '14px',
                            color: theme.palette.darkBlueGray.main,
                          }}
                        >
                          {/* TODO: DON'T SHOW THIS FOR ANNUAL BILLING */}
                          {`Your monthly subscription cost will ${values.farmingStopped ? 'decrease' : 'increase'} by $${numberWithCommas((monthlyRatePerApn / 100).toFixed(2))}`}
                        </div>
                      )}
                      <br />
                      <br />
                    </>
                  )}
                  <div className={classes.contactFieldsContainer}>
                    <ContactFields
                      valueName="newOwnerContact"
                      addressDisplayName="Owner Contact Address"
                      values={values}
                      touched={touched}
                      errors={errors}
                      setFieldValue={setFieldValue}
                      showBirthday={false}
                      showClearAddressButton
                    />
                  </div>
                  <br />
                  <Field
                    name="notes"
                  >
                    {({ field }) => (
                      <CustomInputDashboard
                        labelText="Notes"
                        inputProps={{
                          ...field,
                          multiline: true,
                          rows: 5,
                        }}
                        error={touched[field.name] && errors[field.name] !== undefined}
                      />
                    )}
                  </Field>
                </>

                 )}
              buttons={[
                <ButtonBase
                  onClick={() => setFieldValue('newOwnerContact',
                    {
                      ..._.pick(defaultOwnerContactInfo, ['firstName', 'lastName', 'email', 'phoneNumber']),
                      address: {
                        ...initialAddress,
                        ..._.pick(defaultOwnerContactInfo.address, ['address1', 'address2', 'city', 'state', 'zipcode']),
                      },
                    }, true)}
                  className={classes.cancelButton}
                >
                  Reset Contact Info
                </ButtonBase>,
                <ButtonBase
                  disabled={!isValid || isSubmitting}
                  onClick={() => handleSubmit()}
                >
                  Save
                </ButtonBase>,
              ]}
            />
          </Form>
        );
      }}
    </Formik>
  );
};

EditCampaignEventOverrideModal.propTypes = {
  campaignId: PropTypes.string.isRequired,
  eventId: PropTypes.string.isRequired,
  defaultOwnerContactInfo: PropTypes.object.isRequired,
  existingEventOverride: PropTypes.object,
  onCancel: PropTypes.func.isRequired,
};

export default EditCampaignEventOverrideModal;
